'use client';

import { Stack, Typography } from '@mui/material';
import type { SavedQuery } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface DeleteSavedQueryDialogProps {
  savedQuery: SavedQuery;
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteSavedQueryDialog = ({
  savedQuery,
  workspaceId,
  open,
  onClose,
  onDeleted,
}: DeleteSavedQueryDialogProps) => {
  const dal = useDAL();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await dal.lakehouse.deleteSavedQuery(workspaceId, savedQuery.id);
      onDeleted();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete saved query'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title: 'Delete Saved Query',
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Delete',
          onClick: handleDelete,
          'data-testid': 'delete-saved-query-btn',
          disabled: isLoading,
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-delete-saved-query-btn',
        },
      }}
    >
      <Stack gap='16px' padding='0 40px'>
        <Typography variant='body1'>
          Are you sure you want to delete the saved query{' '}
          <strong>&quot;{savedQuery.name}&quot;</strong>?
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          This action cannot be undone.
        </Typography>

        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
